// src/components/CustomerSummary/panels/HeaderPanel.jsx
// ============================================================================
// BLOCK CS.UI.1 — HeaderPanel (Facility + Date Range + Saved Layouts + Actions)
// ----------------------------------------------------------------------------
// PURPOSE
// - Collect the "context inputs" for Customer Summary:
//     Facility + Ship Date range
// - Provide actions:
//     Generate + Saved Layouts (Load / Save As / Update / Delete)
//
// NOTES
// - "Save" in this panel is "Save Layout" (persistentsitedata), not saving a report.
// - Parent owns actual persistence handlers (controller).
// ============================================================================

import React from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import SmartDateField from '../components/SmartDateField.jsx';

export default function HeaderPanel({
  // Facility
  facilities = [],
  loadingFacilities = false,
  facilityName = '',
  onFacilityChange = () => {},

  // Ship dates (UI/display strings)
  startDate = '',
  endDate = '',
  onStartDateChange = () => {},
  onEndDateChange = () => {},

  // ISO commit (optional but preferred)
  onStartDateCommitIso = () => {},
  onEndDateCommitIso = () => {},

  // Saved layouts
  saves = [], // [{ headerid, savename }]
  selectedSave = '', // headerid (string/number)
  onSelectSave = () => {}, // (headerid) => void
  onSaveAs = () => {}, // (savename) => void
  onUpdate = () => {}, // () => void (updates currently selected save)
  onDelete = () => {}, // () => void (deletes currently selected save)

  // Actions / state
  canGenerate = false,
  generating = false,
  saving = false, // "saving layout" busy flag
  onGenerate = () => {},
  onLoad = () => {},
  canLoad = false,
}) {
  const facilityList = Array.isArray(facilities) ? facilities : [];
  const saveList = Array.isArray(saves) ? saves : [];
  const selectedSaveId = selectedSave == null ? '' : String(selectedSave);
  const hasSelectedSave = Boolean(selectedSaveId);

  const [saveAsOpen, setSaveAsOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [saveAsName, setSaveAsName] = React.useState('');

  const canUpdateOrDelete = Boolean(selectedSaveId);

  const handleOpenSaveAs = React.useCallback(() => {
    setSaveAsName('');
    setSaveAsOpen(true);
  }, []);

  const handleCloseSaveAs = React.useCallback(() => {
    setSaveAsOpen(false);
    setSaveAsName('');
  }, []);

  const handleConfirmSaveAs = React.useCallback(() => {
    const name = String(saveAsName || '').trim();
    if (!name) {
      return;
    }
    onSaveAs(name);
    setSaveAsOpen(false);
    setSaveAsName('');
  }, [onSaveAs, saveAsName]);

  const handleOpenDelete = React.useCallback(() => {
    setDeleteOpen(true);
  }, []);

  const handleCloseDelete = React.useCallback(() => {
    setDeleteOpen(false);
  }, []);

  const handleConfirmDelete = React.useCallback(() => {
    onDelete();
    setDeleteOpen(false);
  }, [onDelete]);

  // ---------------------------------------------------------------------------
  // BLOCK CS.UI.1A — Primary action button (debuggable)
  // ---------------------------------------------------------------------------
  const handlePrimaryClick = React.useCallback(() => {
    // DEBUG (browser console)
    // eslint-disable-next-line no-console
    console.log('[HeaderPanel] primary click', { hasSelectedSave, selectedSaveId });

    if (hasSelectedSave) {
      onLoad(selectedSaveId);
      return;
    }
    onGenerate();
  }, [hasSelectedSave, onLoad, onGenerate, selectedSaveId]);

  let primaryLabel = 'Generate';
  let primaryDisabled = !canGenerate || generating;

  if (hasSelectedSave) {
    primaryLabel = 'Load';
    primaryDisabled = !canLoad;
  } else if (generating) {
    primaryLabel = 'Generating…';
  }

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', padding: 2 }}>
        {/* Facility dropdown (30ch) */}
        <TextField
          select
          label="Facility"
          value={facilityName}
          onChange={(e) => onFacilityChange(e.target.value)}
          disabled={loadingFacilities || facilityList.length === 0}
          sx={{ width: '30ch' }}
          size="small"
        >
          {facilityList.map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </TextField>

        {/* Ship dates */}
        <SmartDateField
          label="From"
          valueDisplay={startDate}
          onChangeDisplay={onStartDateChange}
          onCommitIso={onStartDateCommitIso}
          disabled={false}
        />

        <Typography sx={{ fontWeight: 700 }}>to:</Typography>

        <SmartDateField
          label="To"
          valueDisplay={endDate}
          onChangeDisplay={onEndDateChange}
          onCommitIso={onEndDateCommitIso}
          disabled={false}
        />

        {/* Saved layout dropdown */}
        <TextField
          select
          label="Layout"
          value={selectedSaveId}
          onChange={(e) => onSelectSave(e.target.value)}
          sx={{ width: '26ch' }}
          size="small"
          disabled={saveList.length === 0}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>

          {saveList.map((s) => (
            <MenuItem key={String(s.headerid)} value={String(s.headerid)}>
              {s.savename}
            </MenuItem>
          ))}
        </TextField>

        {/* Actions */}
        <Button
          variant="contained"
          size="small"
          onClick={handlePrimaryClick}
          disabled={primaryDisabled}
        >
          {primaryLabel}
        </Button>

        <Button variant="outlined" size="small" onClick={handleOpenSaveAs} disabled={saving}>
          Save As…
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={onUpdate}
          disabled={!canUpdateOrDelete || saving}
        >
          Update
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={handleOpenDelete}
          disabled={!canUpdateOrDelete || saving}
        >
          Delete
        </Button>
      </Stack>

      {/* Save As dialog */}
      <Dialog open={saveAsOpen} onClose={handleCloseSaveAs} maxWidth="xs" fullWidth>
        <DialogTitle>Save Layout</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Layout name"
            fullWidth
            value={saveAsName}
            onChange={(e) => setSaveAsName(e.target.value)}
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSaveAs} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSaveAs}
            variant="contained"
            disabled={saving || String(saveAsName || '').trim().length === 0}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={deleteOpen} onClose={handleCloseDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Layout?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently delete the selected saved layout.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" disabled={saving}>
            {saving ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
